import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';

import { filter, map } from 'rxjs/operators';
import { EMPTY, fromEvent, merge, Observable } from 'rxjs';

/**
 * Tells a consumer when it is worth trying a dead connection again.
 *
 * Two signals, because neither covers the other: `online` fires when the network stack recovers
 * while the page is running, and a page that was frozen meanwhile — backgrounded tab, locked
 * phone — can miss it entirely and only find out that time passed when it becomes visible again.
 */
@Injectable({
  providedIn: 'root',
})
export class NetworkStatusService {
  /** Emits on every moment worth retrying. Never emits on the server, where there is no network to lose. */
  public readonly resumed$: Observable<void>;

  private readonly _document = inject(DOCUMENT);
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor() {
    this.resumed$ = this._isBrowser ? this._watchResumed$() : EMPTY;
  }

  /**
   * False only when the browser is certain there is no connection. True is not a promise that
   * anything is reachable, so this is only good for skipping attempts that cannot work.
   */
  public get isOnline(): boolean {
    return this._document.defaultView?.navigator?.onLine ?? true;
  }

  private _watchResumed$(): Observable<void> {
    // listens for browser tab/page visibility changes.
    // user switches to another tab → visibilityState = "hidden"
    // user comes back → visibilityState = "visible"
    const visible$ = fromEvent(this._document, 'visibilitychange').pipe(
      filter(() => this._document.visibilityState === 'visible'),
    );

    // Wi-Fi disconnected → offline event
    // Wi-Fi reconnects → "online" event
    const browserOnline$ = fromEvent(this._document.defaultView, 'online');

    return merge(browserOnline$, visible$).pipe(map(() => undefined));
  }
}
